import { Box, Flex, Text } from '@common/fundamentals'
import { IconButton } from '@common/material'
import { RegisterItemInspectionProperty } from '@core/entities/register/RegisterItem'
import { PageUrls } from '@core/page-urls'
import { localizedStrings } from '@core/strings'
import { OpenInNewOutlined, Warning } from '@mui/icons-material'
import { alpha } from '@mui/material'
import moment from 'moment'
import { useRouter } from 'next/router'

type RegisterItemInspectionCardProps = {
  registerItem: RegisterItemInspectionProperty<string>
}

export const RegisterItemInspectionCard: React.FC<
  RegisterItemInspectionCardProps
> = ({ registerItem }) => {
  const router = useRouter()
  const inspection = registerItem?.inspection
  const lastInspected = registerItem?.inspection?.lastInspectionTime
    ? moment(registerItem?.inspection?.lastInspectionTime).format('DD MMM YYYY')
    : null

  const hasNextInspection = !!inspection?.nextInspectionDue
  const nextInspectionDue = moment(inspection?.nextInspectionDue).format(
    'DD MMM YYYY HH:mm',
  )
  const lastInspectedBy = registerItem?.inspection?.lastInspectedBy

  const hasInspectionExpired = moment().isAfter(inspection?.nextInspectionDue)

  const checklist = inspection?.lastSubmittedChecklist

  return (
    <Flex
      bgcolor='#FAFAFA'
      border={`1px solid ${alpha('#000', 0.12)}`}
      py={1}
      px={1.5}
      mb={1.5}
      flexDirection='column'
      gap={2}
      borderRadius='4px'
    >
      <Flex justifyContent='space-between' alignItems='center'>
        <Text fontSize={14} color='text.secondary'>
          {registerItem.name}
        </Text>

        <IconButton
          color='primary'
          sx={{ height: 'fit-content', alignSelf: 'center' }}
          onClick={() =>
            router.push(
              PageUrls.projectInspection({
                organisationId: checklist.organisationId,
                projectId: checklist.project.id,
                checklistId: checklist.id,
              }),
            )
          }
        >
          <OpenInNewOutlined />
        </IconButton>
      </Flex>

      {inspection ? (
        <Flex flexDirection='column' gap={1}>
          <Flex>
            <Text noWrap fontSize={12} color='text.disabled' flex={1}>
              {localizedStrings.lastCompleted}
            </Text>
            <Box ml={2} flex={2}>
              <Text noWrap fontSize={14}>
                {lastInspected ? lastInspected : '---'}
              </Text>
              {lastInspectedBy && (
                <Text
                  noWrap
                  fontSize={14}
                  color='text.secondary'
                >{`${lastInspectedBy.firstName} ${lastInspectedBy.lastName}`}</Text>
              )}
            </Box>
          </Flex>

          {hasNextInspection && (
            <Flex>
              <Text
                noWrap
                fontSize={12}
                color={hasInspectionExpired ? 'error' : 'text.disabled'}
                flex={1}
              >
                {localizedStrings.inspectionDue}
              </Text>
              <Flex ml={2} gap={1} flex={2}>
                {hasInspectionExpired && (
                  <Warning fontSize='small' color='error' />
                )}
                <Text
                  noWrap
                  fontSize={14}
                  color={hasInspectionExpired && 'error'}
                >
                  {nextInspectionDue}
                </Text>
              </Flex>
            </Flex>
          )}
        </Flex>
      ) : (
        <Text fontSize={14}>{localizedStrings.na}</Text>
      )}
    </Flex>
  )
}
